import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Merges class names ("cn") using `clsx` for conditional joining and `tailwind-merge`
 * for intelligently resolving conflicting Tailwind CSS utilities (e.g. `px-4 px-8` → `px-8`).
 * This ensures that consumer-provided `className` overrides base styles deterministically.
 *
 * @example
 * ```ts
 * // Strings
 * cn('flex', 'items-center')                      // → "flex items-center"
 *
 * // Conditional (falsy values are ignored)
 * cn('flex', isActive && 'bg-brand', undefined)    // → "flex bg-brand" (if isActive)
 *
 * // Object syntax (keys with truthy values are included)
 * cn('flex', { 'text-brand': isActive, 'text-primary': !isActive })
 *
 * // Arrays
 * cn(['flex', 'gap-8'], 'p-12')                   // → "flex gap-8 p-12"
 *
 * // Tailwind conflict resolution (tailwind-merge)
 * cn('px-4 py-8', 'px-12')                        // → "py-8 px-12" (px-4 removed)
 * cn('text-sm', className)                         // → consumer className wins on conflicts
 * ```
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Normalizes multiline paragraphs by replacing escaped newlines with actual newlines and splitting the text into an array of paragraphs.
 *
 * @param text - The text to normalize.
 * @returns An array of paragraphs.
 */
export function normalizeMultilineParagraphs(text: string): string[] {
  const normalized = text.replaceAll(String.raw`\n`, '\n');

  return normalized
    .split('\n')
    .map((paragraph) => paragraph.trim())
    .filter((paragraph) => paragraph.length > 0);
}
